import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsappHomePage() {
  const pageTitle = "WhatsApp - Businesses";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [businesses, setBusinesses] = useState([]);
  const [configId, setConfigId] = useState(null);
  const [appId, setAppId] = useState(null);
  const [fbReady, setFbReady] = useState(false);
  const [reqmntsOpenDialog, setReqmntsOpenDialog] = useState(false);

  // Ref to hold postMessage payload across event listeners
  const metaSessionDataRef = useRef(null);

  const tableColumns = [
    { Header: "Business", accessor: "title", align: "left" },
    { Header: "WABA ID", accessor: "waba_id", align: "left" },
    // { Header: "Verification", accessor: "verification", align: "left" },
    // { Header: "Review", accessor: "review", align: "left" },
    { Header: "Webhook", accessor: "webhook", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildRows = (data) => {
    return data.map((row) => ({
      title: row.title,
      waba_id: row.wa_waba_id,
      // verification: row.wa_business_verification_status || "-",
      // review: row.wa_account_review_status || "-",
      webhook: row.wa_webhook_subscribed ? "Subscribed" : "Not subscribed",
      actions: (
        <MDBox mt={1} variant="button">
          <MDButton
            size="small"
            variant="contained"
            color="dark"
            onClick={() => navigate(`/wacc/businesses/${row.reference}`)}
            startIcon={
              <Icon sx={{ cursor: "pointer" }} fontSize="small">
                east
              </Icon>
            }
          >
            View
          </MDButton>
        </MDBox>
      ),
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      try {
        const result = await axios.get(`/portal/wacc/businesses.json`);
        setShowLoading(false);

        if (result.data.status === "success") {
          setConfigId(result.data.data.config_id);
          setAppId(result.data.data.app_id);

          const rows = buildRows(result.data.data.businesses);
          setBusinesses(rows);
          setPageLoaded(true);
        } else {
          statusDialog.show(result.data.data.message, "error", "Request Failed");
        }
      } catch {
        setShowLoading(false);
        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Request Failed"
        );
      }
    };

    fetchData();
  }, [refreshPage]);

  // 1. Listen for Meta Embedded Signup postMessage Events
  useEffect(() => {
    if (!appId) return;

    const handleMetaMessage = (event) => {
      // Validate event source domain
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH") {
            // Stores session parameters: waba_id, phone_number_id, business_id
            metaSessionDataRef.current = data.data;
          }
        }
      } catch (e) {
        // Ignore non-JSON system messages
      }
    };

    window.addEventListener("message", handleMetaMessage);

    const initFB = () => {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: "v25.0",
      });
      setFbReady(true);
    };

    if (window.FB) {
      initFB();
    } else {
      window.fbAsyncInit = initFB;

      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    }

    return () => {
      window.removeEventListener("message", handleMetaMessage);
    };
  }, [appId]);

  // 2. Trigger Tech Provider Onboarding Flow
  const connectBusiness = () => {
    if (!fbReady || !window.FB) {
      statusDialog.show(
        "WhatsApp Embedded Signup is still loading. Please try again.",
        "error",
        "Unable to Connect"
      );
      return;
    }

    if (!configId) {
      statusDialog.show(
        "WhatsApp Embedded Signup configuration is not available.",
        "error",
        "Unable to Connect"
      );
      return;
    }

    // Reset session reference prior to initiating connection
    metaSessionDataRef.current = null;

    window.FB.login(
      (response) => {
        if (!response.authResponse || !response.authResponse.code) {
          statusDialog.show(
            "WhatsApp business connection was cancelled.",
            "error",
            "Connection Cancelled"
          );
          return;
        }

        // Authorization Code from OAuth callback
        const code = response.authResponse.code;
        const sessionData = metaSessionDataRef.current;

        // Meta Session metadata from postMessage
        const wabaId = sessionData?.waba_id;
        const phoneNumberId = sessionData?.phone_number_id;
        const businessId = sessionData?.business_id;

        if (!wabaId || !phoneNumberId) {
          statusDialog.show(
            "WhatsApp onboarding did not return the required business information.",
            "error",
            "Connection Failed"
          );
          return;
        }

        setShowLoading(true);

        // Deliver payload to Django backend for Tech Provider / OBO token exchange
        axios
          .post("/portal/wacc/businesses/connect.json", {
            code: code,
            waba_id: wabaId,
            phone_number_id: phoneNumberId,
            business_id: businessId || null,
          })
          .then(async (result) => {
            setShowLoading(false);

            if (result.data.status === "success") {
              await statusDialog.show(
                "WhatsApp business connected successfully.",
                "success",
                "Success!"
              );
              setRefreshPage((value) => !value);
            } else {
              await statusDialog.show(result.data.data.message, "error", "Connection Failed");
            }
          })
          .catch(async () => {
            setShowLoading(false);
            await statusDialog.show(
              `Unable to complete request. ${
                navigator.onLine ? "" : "Make sure you have an active internet connection"
              }`,
              "error",
              "Request Failed"
            );
          });
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        // auth_type: "reauthenticate", // Force user to re-confirm password/account
        auth_type: "rerequest",
        extras: {
          sessionInfoVersion: 3,
        },
      }
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={reqmntsOpenDialog} fullWidth maxWidth="sm">
        <DialogTitle>Whatsapp Setup CheckList</DialogTitle>
        <DialogContent>
          <Stack mt={1} gap={3}>
            <MDBox display="flex" gap={2} alignItems="start">
              <Icon color="success">business</Icon>
              <MDBox>
                <MDTypography variant="subtitle2" fontWeight="bold">
                  Meta Verified Business Profile
                </MDTypography>
                <MDTypography variant="caption" fontWeight="regular">
                  Verify your meta business profile. Unverified businesses are capped at 250
                  messages until verification is completed.
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox display="flex" gap={2} alignItems="start">
              <Icon color="success">phone</Icon>
              <MDBox>
                <MDTypography variant="subtitle2" fontWeight="bold">
                  Business Phone Number
                </MDTypography>
                <MDTypography variant="caption" fontWeight="regular">
                  Meta will reject numbers already actively used on whatsapp. You must use a brand new
                  number or delete its existing whatsapp account.
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox display="flex" gap={2} alignItems="start">
              <Icon color="success">payment</Icon>
              <MDBox>
                <MDTypography variant="subtitle2" fontWeight="bold">
                  Configured Payment Method
                </MDTypography>
                <MDTypography variant="caption" fontWeight="regular">
                  To send business initiated messages, Meta requires a valid payment method
                  configured on your business profile.
                </MDTypography>
              </MDBox>
            </MDBox>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setReqmntsOpenDialog(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="dark"
            disabled={!fbReady || !configId}
            onClick={() => {
              setReqmntsOpenDialog(false);
              connectBusiness();
            }}
            startIcon={<Icon>add</Icon>}
          >
            Connect Meta Business
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2}>
                <MDBox align="center">
                  <MDBox>
                    <MDTypography variant="h6" fontSize={48}>
                      <Icon>chat</Icon>
                    </MDTypography>

                    <MDTypography variant="h6" fontWeight="bold" fontSize={16}>
                      WhatsApp Communication Center
                    </MDTypography>

                    <MDTypography mt={1} variant="h6" fontWeight="light" fontSize={16}>
                      Manage your business' communication on Whatsapp seamlessly.
                      <br />
                      Monitor and track customer conversations across all team members.
                    </MDTypography>
                  </MDBox>

                  <MDBox mt={3}>
                    <MDButton
                      variant="contained"
                      color="dark"
                      disabled={!fbReady || !configId}
                      // onClick={connectBusiness}
                      onClick={() => setReqmntsOpenDialog(true)}
                      startIcon={<Icon>add</Icon>}
                      size="small"
                    >
                      Connect Meta Business
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>

            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                          Connected Businesses
                        </MDTypography>
                      </MDBox>

                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          disabled={!fbReady || !configId}
                          // onClick={connectBusiness}
                          onClick={() => setReqmntsOpenDialog(true)}
                          size="small"
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <MDBox p={0}>
                    <DataTable
                      table={{
                        columns: tableColumns,
                        rows: businesses,
                      }}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsappHomePage;
